import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import { toastSuccess, toastError } from '../../utils/toast';
import Spinner from '../ui/Spinner';
import logo from '../../assets/logo.png';

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Email viene del paso anterior. Si entran directo, dejamos editable.
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const inputsRef = useRef([]);

  useEffect(() => {
    // Foco automático al primer input del código
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, []);

  const handleCodeChange = (idx, value) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 1);
    const next = [...code];
    next[idx] = sanitized;
    setCode(next);
    if (sanitized && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setCode(next);
    const nextEmptyIdx = Math.min(pasted.length, 5);
    inputsRef.current[nextEmptyIdx]?.focus();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Ingresá el email.');
      return;
    }
    const codeStr = code.join('');
    if (codeStr.length !== 6) {
      setError('Ingresá el código completo de 6 dígitos.');
      return;
    }
    if (newPassword.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${apiUrl}/reset-password`, {
        email: email.trim().toLowerCase(),
        code: codeStr,
        newPassword,
      });
      setSuccess(true);
      toastSuccess('Contraseña actualizada correctamente.');
    } catch (err) {
      const msg = err.response?.data?.error || 'No se pudo resetear la contraseña. Verificá el código y reintentá.';
      setError(msg);
      toastError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-bg p-6 relative overflow-hidden">
        <div className="relative w-full max-w-md">
          <div className="si-card p-8 text-center">
            <div
              className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-5"
              style={{ background: 'rgba(200, 240, 106, 0.15)' }}
            >
              <FaCheckCircle className="text-5xl" style={{ color: '#c8f06a' }} />
            </div>
            <h1 className="text-2xl font-bold text-ink mb-2 tracking-tightish">
              Contraseña actualizada
            </h1>
            <p className="text-sm text-ink-2 mb-6">
              Ya podés iniciar sesión con tu nueva contraseña.
            </p>
            <button
              onClick={() => navigate('/')}
              className="si-btn-primary w-full py-3"
            >
              Ir al login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-bg p-6 relative overflow-hidden">

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img
            src={logo}
            alt="ServiceIn"
            className="w-11 h-11 rounded-xl object-contain"
          />
          <span className="text-xl font-semibold text-ink">ServiceIn</span>
        </div>

        {/* Card */}
        <div className="si-card p-8">
          <h1 className="text-2xl font-bold text-ink mb-1 tracking-tightish">
            Nueva contraseña
          </h1>
          <p className="text-sm text-ink-3 mb-6">
            Ingresá el código de 6 dígitos que enviamos a tu email y elegí una nueva contraseña.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!location.state?.email && (
              <div>
                <label className="si-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="si-input"
                  placeholder="tucorreo@ejemplo.com"
                  autoComplete="email"
                />
              </div>
            )}

            <div>
              <label className="si-label">Código de 6 dígitos</label>
              <div className="flex items-center gap-2 justify-between" onPaste={handleCodePaste}>
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (inputsRef.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(idx, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                    className="si-input w-11 h-12 text-center text-lg font-semibold p-0"
                    aria-label={`Dígito ${idx + 1}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="si-label" htmlFor="newPassword">Nueva contraseña</label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="si-input pr-10"
                  placeholder="Mínimo 6 caracteres"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink transition"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div>
              <label className="si-label" htmlFor="confirmPassword">Confirmar contraseña</label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="si-input"
                placeholder="Repetí la contraseña"
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div className="rounded-xl border border-danger/30 bg-danger/10 px-3 py-2.5 text-sm text-danger">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="si-btn-primary w-full py-3"
            >
              {loading ? (
                <>
                  <Spinner size={16} className="mr-2" />
                  Guardando...
                </>
              ) : (
                'Cambiar contraseña'
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-line text-center">
            <Link
              to="/"
              className="text-sm text-ink-2 hover:text-ink transition inline-flex items-center gap-2"
            >
              <FaArrowLeft className="text-xs" />
              Volver al login
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-ink-3 mt-6">
          ServiceIn · Gestión de propinas
        </p>
      </div>
    </div>
  );
}

export default ResetPassword;
